import { runInAction, makeAutoObservable, action } from 'mobx';
import { Auth, AuthLoginResponseProps, Profile, ProfileType } from '@watcherr/resources';

import { State } from '~/store/types';
import UserProfileEntry from './Profile/UserProfileEntry';
import * as Sentry from '@sentry/react';
import TokenService from '~services/TokenService';
import PersistentStoreService from '~services/PersistentStoreService';

class AuthStore {
  rootStore: State;
  error: Error | null = null;
  userType?: ProfileType;

  isLoggedIn: boolean = false;
  loading: boolean = true;
  user?: UserProfileEntry | null = null;
  isInitialized = false;

  constructor(rootStore: State) {
    makeAutoObservable(this, {
      init: action.bound,
      logout: action.bound,
      login: action.bound,
      setTokenData: action.bound,
      fetchUser: action.bound,
      rootStore: false,
    });

    this.rootStore = rootStore;
  }

  init() {
    TokenService.getAuthResponse()
      .then((parsed) => {
        if (parsed && new Date(parsed.expiryDate) > new Date()) {
          this.setTokenData(parsed);
        } else {
          this.logout();
        }
      })
      .catch((e) => {
        console.error(e);
        this.logout();
      })
      .finally(() => {
        runInAction(() => {
          this.loading = false;
          this.isInitialized = true;
        });
      });
  }

  async logout() {
    const user = this.user;

    this.rootStore.reset();

    this.isLoggedIn = false;
    this.isInitialized = true;
    this.user = undefined;
    this.userType = undefined;

    if (user) {
      Auth.logout().then(console.info).catch(console.error);
    }
    try {
      await Promise.all([TokenService.clear(), PersistentStoreService.clear()]);
    } catch (err) {
      console.error('logout -> clear services', err);
    }
  }

  login(username: string, password: string) {
    this.loading = true;
    Auth.login({ email: username, password: password })
      .then((response) => {
        if (
          [
            ProfileType.HealthcareProvider,
            ProfileType.Installer,
            ProfileType.WatcherrEmployee,
            ProfileType.Other,
          ].includes(response.profileType)
        ) {
          this.setTokenData(response);
        } else {
          this.loading = false;
          this.error = new Error('Did not include profile type');
        }
      })
      .catch((error) => {
        this.loading = false;

        this.error = error;
      });
  }

  setTokenData(data: AuthLoginResponseProps) {
    TokenService.setAuthResponse(data);
    this.userType = data.profileType;
    // this.rootStore.permissions.loadPermissions();
    this.fetchUser(data.profileId, () => {
      runInAction(() => {
        this.loading = false;
      });
    });
    this.isInitialized = true;
    this.isLoggedIn = true;
  }

  fetchUser(profileId: number, cb) {
    Profile.getProfile(profileId)
      .then((data) => {
        const userEntry = new UserProfileEntry(this.rootStore, data);
        runInAction(() => {
          this.user = userEntry;
          Sentry.setUser({ id: data.uuid });
        });
        cb();
      })
      .catch((e) => {
        console.error(e);
        this.logout();
      });
  }
}

export default AuthStore;
